/**
 * `t()` и подключение словарей. Один активный язык на страницу: выбирается при старте (./locale.ts) и меняется
 * `setLocale` (переключатель языка — перерисовка подписчиков через `onLocaleChange`).
 *
 * Тексты своих экранов владельцы (HUD, окна, вкладки) подключают сами:
 *
 *   const msgs = { en: { "hud.autoSkills": "Auto" }, ru: { "hud.autoSkills": "Авто" }, uk: { "hud.autoSkills": "Авто" } };
 *   addMessages(msgs);           // TypeScript требует одинаковые ключи во всех языках
 *   t("hud.autoSkills");
 */
import type { Locale } from "@newfungame/contracts";
import { DEFAULT_LOCALE, NUMBER_LOCALES } from "./locale";
import { en, ru, uk, type CoreKey, type Messages, type PluralForm } from "./messages";

/** Параметры подстановки: `{name}` в тексте. `count` ещё и выбирает форму числа. */
export type TParams = Record<string, string | number>;

/** Словари модуля на все языки с одинаковыми ключами. */
export interface MessageBundle<K extends string> {
  en: Messages<K>;
  ru: Messages<K>;
  uk: Messages<K>;
}

const dictionaries: Record<Locale, Record<string, string>> = { en: { ...en }, ru: { ...ru }, uk: { ...uk } };
let current: Locale = DEFAULT_LOCALE;
const listeners = new Set<(l: Locale) => void>();
const pluralRules = new Map<Locale, Intl.PluralRules>();

/** Ключ: свой словарь оболочки или любой подключённый через `addMessages`. */
export type TKey = CoreKey | (string & {});

/** Подключить тексты модуля. Повторный ключ перезаписывает прежний (последний `addMessages` побеждает). */
export function addMessages<K extends string>(bundle: MessageBundle<K>): void {
  Object.assign(dictionaries.en, bundle.en);
  Object.assign(dictionaries.ru, bundle.ru);
  Object.assign(dictionaries.uk, bundle.uk);
}

export function getLocale(): Locale {
  return current;
}

/** Сменить язык; подписчики получают новый. Без изменения — ничего не делает. */
export function setLocale(locale: Locale): void {
  if (locale === current) return;
  current = locale;
  for (const l of listeners) l(locale);
}

export function onLocaleChange(listener: (l: Locale) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function pluralForm(locale: Locale, n: number): PluralForm {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules.select(n) as PluralForm;
}

function lookup(locale: Locale, key: string, params?: TParams): string | undefined {
  const dict = dictionaries[locale];
  const count = params?.count;
  if (typeof count === "number") {
    const form = dict[`${key}.${pluralForm(locale, count)}`] ?? dict[`${key}.other`];
    if (form !== undefined) return form;
  }
  return dict[key];
}

/**
 * Текст по ключу на заданном языке. Нет перевода — английский; нет и его — сам ключ (видно на экране, ловит e2e).
 */
export function translate(locale: Locale, key: TKey, params?: TParams): string {
  const raw = lookup(locale, key, params) ?? lookup("en", key, params) ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = params[name];
    if (v === undefined) return m;
    return typeof v === "number" ? v.toLocaleString(NUMBER_LOCALES[locale]) : v;
  });
}

/** Текст по ключу на текущем языке. */
export function t(key: TKey, params?: TParams): string {
  return translate(current, key, params);
}

/** Есть ли ключ в словаре языка (тесты и проверки полноты). */
export function hasMessage(locale: Locale, key: string): boolean {
  return key in dictionaries[locale];
}
